import { useCallback, useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import DataTable, { type DataTableColumn } from '../components/common/DataTable';
import { blockBackdropClose, FormDialogTitle, LoadingState } from '../components/common';
import {
  memberService,
  type HomepageMember,
  type HomepageMemberUpdateRequest,
} from '../services/memberService';
import { formatPhoneNumberForDisplay } from '../utils/phoneNumber';

const PAGE_SIZE = 20;

const GENDER_OPTIONS = [
  { label: '남성', value: 'MALE' },
  { label: '여성', value: 'FEMALE' },
];

const emptyForm: HomepageMemberUpdateRequest = {
  name: '',
  gender: '',
  birthDate: '',
};

function getGenderLabel(gender: string | null) {
  if (!gender) return '-';
  return GENDER_OPTIONS.find((option) => option.value === gender)?.label ?? gender;
}

function HomepageUserManagementPage() {
  const [members, setMembers] = useState<HomepageMember[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeMember, setActiveMember] = useState<HomepageMember | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HomepageMember | null>(null);
  const [form, setForm] = useState<HomepageMemberUpdateRequest>(emptyForm);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dialogErrorMessage, setDialogErrorMessage] = useState('');

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await memberService.list(page, PAGE_SIZE, appliedKeyword);
      setMembers(response.members);
      setTotalCount(response.totalCount);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '사용자 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [appliedKeyword, page]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(0);
    setAppliedKeyword(keyword.trim());
  };

  const handleResetSearch = () => {
    setKeyword('');
    setAppliedKeyword('');
    setPage(0);
  };

  const openEditDialog = (member: HomepageMember) => {
    setActiveMember(member);
    setForm({
      name: member.name,
      gender: member.gender ?? '',
      birthDate: member.birthDate ?? '',
    });
    setDialogErrorMessage('');
  };

  const closeEditDialog = () => {
    if (!saving) {
      setActiveMember(null);
      setForm(emptyForm);
      setDialogErrorMessage('');
    }
  };

  const handleUpdate = async () => {
    if (!activeMember) return;
    const name = form.name?.trim();
    if (!name) {
      setDialogErrorMessage('이름을 입력해 주세요.');
      return;
    }

    setSaving(true);
    try {
      await memberService.update(activeMember.id, {
        name,
        ...(form.gender ? { gender: form.gender } : {}),
        ...(form.birthDate ? { birthDate: form.birthDate } : {}),
      });
      setActiveMember(null);
      setForm(emptyForm);
      setDialogErrorMessage('');
      setSuccessMessage('사용자 정보를 수정했습니다.');
      await loadMembers();
    } catch (error) {
      setDialogErrorMessage(error instanceof Error ? error.message : '사용자 정보를 수정하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await memberService.delete(deleteTarget.id);
      setDeleteTarget(null);
      setSuccessMessage('사용자를 삭제했습니다.');
      if (members.length === 1 && page > 0) {
        setPage((current) => current - 1);
      } else {
        await loadMembers();
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '사용자를 삭제하지 못했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const columns: DataTableColumn<HomepageMember>[] = [
    { key: 'name', header: '이름', sortable: true, width: 180 },
    {
      key: 'phoneNumber',
      header: '휴대폰 번호',
      sortable: true,
      width: 180,
      render: (member) => formatPhoneNumberForDisplay(member.phoneNumber),
    },
    {
      key: 'gender',
      header: '성별',
      sortable: true,
      width: 100,
      render: (member) => getGenderLabel(member.gender),
    },
    { key: 'birthDate', header: '생년월일', sortable: true, width: 140 },
  ];

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <div className="page-title-block">
          <p className="page-section">Homepage</p>
          <h1>홈페이지 사용자 관리</h1>
          <p className="page-description">홈페이지 가입자의 정보를 조회하고 수정하거나 삭제합니다.</p>
        </div>

        {errorMessage && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>{errorMessage}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>}

        <Paper variant="outlined" sx={{ borderColor: '#e2e8f0', boxShadow: 'none', mb: 2, p: 2 }}>
          <Box component="form" onSubmit={handleSearch} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, justifyContent: 'space-between' }}>
            <Box sx={{ color: '#475569', fontSize: 13, fontWeight: 700 }}>총 {totalCount}명</Box>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 450 } }}>
              <TextField fullWidth inputProps={{ maxLength: 100 }} label="이름 검색" onChange={(event) => setKeyword(event.target.value)} size="small" value={keyword} />
              <Button disabled={loading} sx={{ flexShrink: 0, minWidth: 72 }} type="submit" variant="contained">검색</Button>
              <Button disabled={loading} onClick={handleResetSearch} sx={{ flexShrink: 0, minWidth: 72 }} type="button" variant="outlined">초기화</Button>
            </Box>
          </Box>
        </Paper>

        {loading ? (
          <LoadingState message="사용자 목록을 불러오는 중입니다." />
        ) : (
          <>
            <DataTable
              actions={[
                { label: '수정', onClick: openEditDialog },
                { label: '삭제', color: 'error', onClick: setDeleteTarget },
              ]}
              columns={columns}
              emptyMessage={appliedKeyword ? '검색 조건에 맞는 사용자가 없습니다.' : '등록된 홈페이지 사용자가 없습니다.'}
              onRowClick={openEditDialog}
              rowKey="id"
              rows={members}
            />
            {pageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={pageCount}
                  onChange={(_, nextPage) => setPage(nextPage - 1)}
                  page={page + 1}
                  shape="rounded"
                />
              </Box>
            )}
          </>
        )}
      </div>

      <Dialog fullWidth maxWidth="sm" onClose={blockBackdropClose(closeEditDialog)} open={Boolean(activeMember)}>
        <FormDialogTitle onClose={closeEditDialog} sx={{ fontSize: 20, fontWeight: 800 }}>
          사용자 정보 수정
        </FormDialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            {dialogErrorMessage && <Alert severity="warning">{dialogErrorMessage}</Alert>}
            <TextField label="이름" required value={form.name ?? ''} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
            <TextField helperText="휴대폰 번호는 본인 인증 정보이므로 변경할 수 없습니다." InputProps={{ readOnly: true }} label="휴대폰 번호" value={formatPhoneNumberForDisplay(activeMember?.phoneNumber, '')} />
            <TextField InputLabelProps={{ shrink: true }} label="성별" select SelectProps={{ native: true }} value={form.gender ?? ''} onChange={(event) => setForm((current) => ({ ...current, gender: event.target.value }))}>
              <option value="">선택</option>
              {GENDER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </TextField>
            <TextField InputLabelProps={{ shrink: true }} label="생년월일" type="date" value={form.birthDate ?? ''} onChange={(event) => setForm((current) => ({ ...current, birthDate: event.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={saving} onClick={closeEditDialog} variant="outlined">취소</Button>
          <Button disabled={saving} onClick={handleUpdate} variant="contained">{saving ? '수정 중...' : '수정'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="xs" onClose={() => !deleting && setDeleteTarget(null)} open={Boolean(deleteTarget)}>
        <DialogTitle sx={{ fontSize: 20, fontWeight: 800 }}>사용자 삭제</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ color: '#374151', fontSize: 14, lineHeight: 1.7 }}>
            {deleteTarget?.name} 사용자를 삭제할까요? 삭제한 정보는 복구할 수 없습니다.
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={deleting} onClick={() => setDeleteTarget(null)} variant="outlined">취소</Button>
          <Button color="error" disabled={deleting} onClick={handleDelete} variant="contained">{deleting ? '삭제 중...' : '삭제'}</Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default HomepageUserManagementPage;
